import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Academy } from '../../hooks/api/academy';
import type { InstructorLessonsViewMode } from './InstructorDesktopLessonsView';

interface InstructorLessonsHeaderProps {
  academies: Academy[];
  academyId: number;
  date: string;
  lessonsCount: number;
  occupancy: number;
  templateLessonsCount: number;
  templateSeats: number;
  totalSeats: number;
  viewMode: InstructorLessonsViewMode;
  onAcademyChange: (academyId: number) => void;
  onCreateLesson: () => void;
  onDateChange: (date: string) => void;
}

interface StatProps {
  label: string;
  value: string | number;
}

function Stat({ label, value }: StatProps) {
  return (
    <Box sx={{ minWidth: 64 }}>
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontSize: 10,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: 'rgba(17,17,17,0.56)',
          lineHeight: 1,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 700, fontSize: 20, lineHeight: 1, color: '#111111' }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function InstructorLessonsHeader({
  academies,
  academyId,
  date,
  lessonsCount,
  occupancy,
  templateLessonsCount,
  templateSeats,
  totalSeats,
  viewMode,
  onAcademyChange,
  onCreateLesson,
  onDateChange,
}: InstructorLessonsHeaderProps) {
  const stats =
    viewMode === 'generated'
      ? [
          { label: 'Clases', value: lessonsCount },
          { label: 'Ocupación', value: `${occupancy}%` },
          { label: 'Plazas', value: totalSeats },
        ]
      : [
          { label: 'Clases', value: templateLessonsCount },
          { label: 'Plazas', value: templateSeats },
        ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2 },
        mb: 2,
        borderRadius: 4,
        border: '1px solid rgba(18,18,18,0.08)',
        backgroundColor: 'white',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', lg: 'center' }}
        justifyContent="space-between"
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          sx={{ flex: 1 }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel htmlFor="academy-selector">Academia</InputLabel>
            <Select
              id="academy-selector"
              label="Academia"
              value={academyId}
              onChange={(event) => onAcademyChange(Number(event.target.value))}
            >
              {academies.map((academy) => (
                <MenuItem key={academy.id} value={academy.id}>
                  {academy.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {viewMode === 'generated' && (
            <TextField
              name="date"
              type="date"
              size="small"
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              value={date}
              onChange={(event) => onDateChange(event.target.value)}
              sx={{ width: { xs: '100%', sm: 170 } }}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          spacing={{ xs: 2, md: 3 }}
          alignItems="center"
          divider={
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: 'rgba(18,18,18,0.08)' }}
            />
          }
          sx={{ px: { xs: 0, lg: 1 } }}
        >
          {stats.map((stat) => (
            <Stat key={stat.label} label={stat.label} value={stat.value} />
          ))}
        </Stack>

        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={onCreateLesson}
          sx={{
            px: 2.25,
            py: 1,
            borderRadius: 2.5,
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: '#111111',
            color: 'white',
            boxShadow: 'none',
            whiteSpace: 'nowrap',
            '&:hover': {
              bgcolor: '#222222',
              boxShadow: 'none',
            },
          }}
        >
          Añadir clase
        </Button>
      </Stack>
    </Paper>
  );
}
